import type { JSX } from 'react'
import type { ITextBoxProps } from 'atoms/TextBox'
import type { ICard } from '../Card'
import type { CardAnimations, CardsGradient } from '../Card.h'

import { Label, LabelFonts } from 'atoms/Label'
import { LabelStatusBadge } from 'atoms/Label/Templates'
import { OverlayLayoutColors } from 'atoms/OverlayLayout'
import { PartnerProgramCoverDesktop } from 'atoms/PartnerProgramCover'
import {
  TextAligning,
  TextBox,
  TextBoxColors,
  TextBoxTypes,
} from 'atoms/TextBox'
import { Colors } from 'atoms/TextBox/TextBox.h'
import { useBooleanState } from 'utils/hooks'
import { COLOR, FIXED_INDENTS } from 'vars/index'
import { ImageBackgroundSC } from '../base-card'
import { Card } from '../Card'
import { CARDS_ASPECT_RATIO, CardCorners, CardType } from '../Card.h'
import { getTransformScale } from '../Templates/MovieCardDefault/lib'
import {
  CardContentWrapperSC,
  ContentLabelSC,
  LiveLabelSC,
} from './LiveEventCard.styled'

export interface ILiveEventCard extends Pick<
  ICard,
  'lazyImage' | 'posterUrl' | 'rented' | 'scaleOnHover' | 'aclBlockedBy'
> {
  title?: string
  width?: string
  aspectRatio?: (typeof CARDS_ASPECT_RATIO)[keyof typeof CARDS_ASPECT_RATIO]
  children?: JSX.Element
  childrenTransition?: CardAnimations
  transitionName?: CardAnimations

  gradient?: (typeof CardsGradient)[keyof typeof CardsGradient]
  corners?: CardCorners

  font?: ITextBoxProps['font']
  textAlignment?: ITextBoxProps['alignment']
  textColor?: ITextBoxProps['color']
  textMaxLines?: ITextBoxProps['maxLines']

  posterEnabled?: boolean
  overflowEnabled?: boolean
  hidePriceOnHover?: boolean
  purchaseTitle?: string
  titleTimeLabel?: string
  slidingTimeLabel?: boolean
  isLive?: boolean
  rowGap?: FIXED_INDENTS
  description?: string
  gridCard?: boolean
  liveTitle?: string
  descriptionFont?: TextBoxTypes
  labelFont?: LabelFonts
  paid?: boolean
}

export const LiveEventCard = ({
  title = '',
  width = '100%',
  aspectRatio = CARDS_ASPECT_RATIO.HORIZONTAL_DEFAULT,
  children: _unused1,
  transitionName,
  corners = CardCorners.CORNERS_10,

  font: _unused2 = TextBoxTypes.Headline3,
  textAlignment: _unused3 = TextAligning.Center,
  textColor: _unused4 = TextBoxColors.TEXT_ICON_PRIMARY,
  textMaxLines: _unused5 = 2,

  titleTimeLabel,
  slidingTimeLabel = false,
  hidePriceOnHover: _unused6,
  isLive,
  rowGap = FIXED_INDENTS.INDENT_30,
  description,
  gridCard,
  liveTitle = 'Live',
  descriptionFont = TextBoxTypes.FontSmBold,
  labelFont = LabelFonts.Caption4,
  purchaseTitle,
  posterUrl,
  paid,
  rented: _unused9,
  scaleOnHover = false,
  ...rest
}: ILiveEventCard): JSX.Element => {
  const [isPosterLoaded, handlePosterLoading, handlePosterError] =
    useBooleanState(true)

  const transformScale = scaleOnHover
    ? getTransformScale(CardType.HORIZONTAL)
    : 1

  const poster = isPosterLoaded ? posterUrl : ''
  const emptyPoster = !poster || !isPosterLoaded

  const BackgroundCover = emptyPoster ? (
    <ImageBackgroundSC>
      <PartnerProgramCoverDesktop />
    </ImageBackgroundSC>
  ) : (
    <></>
  )

  const posterContent = (
    <>
      <ContentLabelSC slidingLabel={slidingTimeLabel}>
        {titleTimeLabel && (
          <Label
            title={titleTimeLabel!}
            color={COLOR.TEXT_ICON_PRIMARY}
            font={labelFont}
            sliding={slidingTimeLabel}
          />
        )}
      </ContentLabelSC>
      {isLive && (
        <LiveLabelSC>
          <LabelStatusBadge title={liveTitle} data-testid='test-live-label' />
        </LiveLabelSC>
      )}
    </>
  )

  return (
    <CardContentWrapperSC
      rowGap={rowGap}
      gridCard={gridCard}
      posterAspectRatio={aspectRatio}
      scaleOnHover={scaleOnHover}
    >
      <Card
        {...rest}
        posterUrl={poster}
        aspectRatio={aspectRatio}
        width={width}
        transitionName={transitionName}
        corners={corners}
        overlayEnabled={true}
        overlayOnHoverEnabled={true}
        overlayGradient={OverlayLayoutColors.EVENT_CARD_VERTICAL_OVERLAY}
        hidePriceOnHover={false}
        backgroundColorHovered={COLOR.SURFACE_OPACITY_DARK_25}
        purchaseTitle={purchaseTitle}
        isAgeRating={Boolean(title)}
        ageRatingLabel={title}
        posterContent={posterContent}
        backgroundCover={BackgroundCover}
        handlePosterLoading={handlePosterLoading}
        handlePosterError={handlePosterError}
        paid={paid}
        transformScale={transformScale}
      />
      {description && (
        <TextBox
          title={description}
          color={Colors.TEXT_ICON_PRIMARY}
          font={descriptionFont}
          alignment={TextAligning.Left}
          maxLines={2}
        />
      )}
    </CardContentWrapperSC>
  )
}
